import { Injectable, OnDestroy } from '@angular/core';
import { NoNewVersionDetectedEvent, SwUpdate, VersionEvent, VersionReadyEvent } from '@angular/service-worker';
import { AppVersionService } from '@app/core/services/app-version.service';
import { PhoneService } from '@app/phone/services/phone.service';
import { compareSemver } from '@app/shared/utils/update.util';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import {
  BehaviorSubject,
  combineLatest,
  delay,
  filter,
  from,
  map,
  of,
  share,
  Subject,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';

import { AppFeature } from '../models/config.models';
import { AppConfigService } from './app-config.service';

interface VersionEventAppData {
  minVersion: string;
  minElectronVersion: string;
}

@Injectable({
  providedIn: 'root',
})
export class UpdateService implements OnDestroy {
  private readonly updateRejectedSubject = new BehaviorSubject<boolean>(false);
  public updateRejected$ = this.updateRejectedSubject.asObservable();
  private processedNoNewVersionDetectedHashes = new Set<string>();
  private readonly updateActionSubject = new Subject<() => void>();
  private readonly destroySubject = new Subject<void>();

  private userForced = false;

  public get isUpdateRejected() {
    return this.updateRejectedSubject.value;
  }

  constructor(
    private swUpdate: SwUpdate,
    private translateService: TranslateService,
    private appVersionService: AppVersionService,
    private appConfigService: AppConfigService,
    private phoneService: PhoneService
  ) {
    this.updateActionSubject
      .pipe(
        switchMap((action) =>
          combineLatest([this.phoneService.visibleCalls$, this.phoneService.incomingCalls$]).pipe(
            filter(([visible, incoming]) => visible.length === 0 && incoming.length === 0),
            take(1),
            map(() => action)
          )
        ),
        takeUntil(this.destroySubject)
      )
      .subscribe((action) => action());

    if (this.swUpdate.isEnabled) {
      // share() makes the intent explicit and ensures a single source subscription is shared between the two downstream pipelines
      const versionUpdates$ = this.swUpdate.versionUpdates.pipe(share());

      versionUpdates$
        .pipe(
          filter((data): data is VersionReadyEvent => data.type === 'VERSION_READY'),
          // Capture and reset userForced synchronously before any async work
          map((data) => {
            const userForced = this.userForced;
            this.userForced = false;
            return { data, userForced };
          }),
          // Check if min version requires mandatory update
          switchMap(({ data, userForced }) =>
            from(this.minVersionHigherThanCurrent(data)).pipe(
              map((mustUpdate) => ({ data, skipDelay: mustUpdate || userForced }))
            )
          ),
          // Wait for features to load (only needed for delayed updates, but harmless for forced)
          switchMap(({ data, skipDelay }) =>
            skipDelay
              ? of({ data, skipDelay })
              : this.appConfigService.featuresLoaded$.pipe(
                  take(1),
                  map(() => ({ data, skipDelay }))
                )
          ),
          // Apply delay based on update window
          switchMap(({ data, skipDelay }) => {
            if (skipDelay) {
              return of(data);
            }
            const delay_ = this.getDelay();
            if (delay_ instanceof Date) {
              this.updateRejectedSubject.next(true);
            }
            return of(data).pipe(delay(delay_));
          })
        )
        .subscribe(async (data) => {
          this.handleVersionReady(data);
        });

      versionUpdates$
        .pipe(filter((data): data is NoNewVersionDetectedEvent => data.type === 'NO_NEW_VERSION_DETECTED'))
        .subscribe(async (data) => {
          this.handleNoNewVersionDetected(data);
        });
    }
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  public async checkUpdate(userForced = false) {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    /**
     * Subscribe to version updates and then call `checkForUpdate` manually. Calling manually will ensure the check happens at
     * launch. While the check will return a boolean indicating the presence of an update, it does not have the `appData` payload
     * we need to determine if the update should be optional or required.
     *
     * Manually calling checkForUpdate is necessary instead of only subscribing to `versionUpdates` (how it was done previously)
     * since the time it takes for that event to be emitted is outside the bounds of what we want. With this approach, the app's manifest file
     * is downloaded immediately (as part of the check) and triggers a version update.
     */

    this.userForced = userForced;
    console.log('[UPDATE-SERVICE] Check for update forced by user: ', userForced);

    const result = await this.swUpdate.checkForUpdate();
    console.log('[UPDATE-SERVICE] Check for update result:', result);
  }

  private getDelay(): Date | number {
    const updateFrom = moment(this.appConfigService.features[AppFeature.ClientUpdateStartTime], 'h:mm a');
    const updateTo = moment(this.appConfigService.features[AppFeature.ClientUpdateStopTime], 'h:mm a');
    const now = moment();

    if (
      (updateFrom.isBefore(updateTo) && now.isBetween(updateFrom, updateTo)) ||
      (updateTo.isBefore(updateFrom) && !now.isBetween(updateTo, updateFrom))
    ) {
      // within update window
      console.log('[UPDATE-SERVICE] Update delay: instant');
      return 0;
    } else if (now.isBefore(updateFrom)) {
      // same day, just wait until updateFrom
      const delayed = updateFrom.toDate();
      console.log('[UPDATE-SERVICE] Update delay: until ', delayed.toUTCString());
      return delayed;
    } else {
      // wait until next day
      const nextDay = updateFrom.add(1, 'day').toDate();
      console.log('[UPDATE-SERVICE] Update delay: until ', nextDay.toUTCString());
      return nextDay;
    }
  }

  private async handleVersionReady(data: VersionReadyEvent) {
    console.log(`[UPDATE-SERVICE] Version ready: ${data.type}`);

    // If the hashes are the same, don't prompt the user to update.
    if (data.currentVersion.hash === data.latestVersion.hash) {
      return;
    }

    // If there's no appData, only an optional update is needed
    const latestAppData = data.latestVersion.appData as VersionEventAppData;
    if (!latestAppData) {
      console.log('[UPDATE-SERVICE] No appData found in latest version, prompting optional web update');
      this.updateActionSubject.next(() => this.promptOptionalWebUpdate());
      return;
    }

    // If the min version is greater than the current version, force the user to update. Otherwise
    // the update is optional
    const currentVersion = await this.appVersionService.getVersion();
    const mustWebUpdate = this.compareSemver(latestAppData.minVersion, currentVersion) > 0;

    if (mustWebUpdate) {
      console.log('[UPDATE-SERVICE] Must update web version, prompting force web update');
      this.updateActionSubject.next(() => this.promptForceWebUpdate());
    } else {
      console.log('[UPDATE-SERVICE] Optional update available, prompting optional web update');
      this.updateActionSubject.next(() => this.promptOptionalWebUpdate());
    }
  }

  private async minVersionHigherThanCurrent(data: VersionEvent): Promise<boolean> {
    if (data.type === 'VERSION_READY' && data.currentVersion.hash !== data.latestVersion.hash) {
      const latestAppData = data.latestVersion.appData as VersionEventAppData;

      if (latestAppData) {
        const currentVersion = await this.appVersionService.getVersion();
        console.log(
          '[UPDATE-SERVICE] Must update (with latestAppData):',
          this.compareSemver(latestAppData.minVersion, currentVersion) > 0
        );
        return this.compareSemver(latestAppData.minVersion, currentVersion) > 0;
      }
    }

    console.log('[UPDATE-SERVICE] Must update:', false);
    return false;
  }

  private async handleNoNewVersionDetected(data: NoNewVersionDetectedEvent) {
    console.log(`[UPDATE-SERVICE] No new version detected: ${data.type}`);

    // Skip if we've already processed this hash
    if (this.processedNoNewVersionDetectedHashes.has(data.version.hash)) {
      console.log(`[UPDATE-SERVICE] Skipping duplicate NO_NEW_VERSION_DETECTED for hash: ${data.version.hash}`);
      return;
    }

    // Mark this hash as processed
    this.processedNoNewVersionDetectedHashes.add(data.version.hash);
  }

  private promptForceWebUpdate() {
    const message = this.translateService.instant('UPDATE_SERVICE.CRITICAL_UPDATE_ALERT');
    alert(message);
    this.reloadWindow();
  }

  private promptOptionalWebUpdate() {
    const message = this.translateService.instant('UPDATE_SERVICE.OPTIONAL_UPDATE_CONFIRM');
    if (confirm(message)) {
      this.reloadWindow();
    } else {
      this.updateRejectedSubject.next(true);
    }
  }

  /** Abstracted to allow for unit testing which cannot properly spy on `window.location` */
  private reloadWindow() {
    window.location.reload();
  }

  /** Abstracted to allow for unit testing which cannot properly mock a plain exported function */
  private compareSemver(version1: string, version2: string) {
    return compareSemver(version1, version2);
  }
}
